import { audio } from '../systems/audio/AudioSystem.js';

/**
 * The routes into a run, and **when each one starts being a real choice**.
 *
 * Every mode here is gated on something the player has actually done. That is
 * not artificial scarcity; it is the fix for what this screen used to be. A
 * brand-new save — zero Echoes, zero Shards, zero Glimmer, no run ever
 * finished — was shown seven routes, and five of them could not mean anything
 * yet: a Leaderboard with no score on it, a Reprise world the epilogue hasn't
 * introduced, a Seeded Run whose entire appeal is comparing against a run you
 * have never done, a Boss Challenge listing bosses you have never met, and
 * Card Practice for attack patterns you have never seen. Seven equal-weight
 * buttons, two of them usable. The genre's own opening screen is one button.
 *
 * Locked routes are *shown, greyed, with their condition stated*, rather than
 * hidden. Hiding would trade one problem for a worse one — a player who never
 * learns the game has a daily seed or a shared world — and champion select
 * already establishes locked-but-legible as this game's idiom for "not yet".
 *
 * The conditions are deliberately shallow. Every one of them falls in the
 * first session or two; this is about the first ten minutes not being a wall,
 * not about withholding content.
 */
const MODES = [
  {
    action: 'start',
    icon: '▶',
    title: 'START RUN',
    blurb: 'Choose a champion and enter the normal or Ascension gauntlet.',
    primary: true,
  },
  {
    action: 'tide',
    icon: '≈',
    title: 'UNDERSONG TIDE',
    blurb: 'One arena, twenty minutes, a rising tide — and something at the end of the clock.',
    // Never gated. It is the gentlest thing in the game — no telegraph
    // reading, no gauntlet, just movement and motes — and BULLET-HEAVEN.md
    // §H7 argues it is a better first run than a cold open into a boss
    // gauntlet. Locking it would be locking the on-ramp.
  },
  {
    action: 'seeded-run',
    icon: '☀',
    title: 'SEEDED RUN',
    blurb: "Today's daily, or a seed you type — the same waves, cards and offers for everyone who plays it.",
    unlocked: (save) => save.statistics.runs > 0,
    requirement: 'Finish a run first.',
  },
  {
    action: 'boss-challenge',
    icon: '⚔',
    title: 'BOSS CHALLENGE',
    blurb: 'Jump straight into a single boss fight — no waves, practice only.',
    unlocked: (save) => save.statistics.defeatedBosses.length > 0,
    requirement: 'Defeat a boss first.',
  },
  {
    action: 'card-practice',
    icon: '✦',
    title: 'CARD PRACTICE',
    blurb: 'Drill one boss attack on repeat until you can capture it.',
    unlocked: (save) => save.statistics.defeatedBosses.length > 0,
    requirement: 'Defeat a boss first — its attacks unlock with it.',
  },
  {
    action: 'leaderboard',
    icon: '≡',
    title: 'LEADERBOARD',
    blurb: 'Compare clears, Ascension runs, and Endless depth.',
    unlocked: (save) => save.statistics.runs > 0,
    requirement: 'Finish a run first — you need a score to compare.',
  },
  {
    action: 'reprise',
    icon: '❖',
    title: 'REPRISE',
    blurb: 'Rebuild the unmade world together — every run pours Resonance into it.',
    // The mode's own pitch is "after The Unmade King falls, the world is
    // still unmade" (REPRISE.md). Offering it before the King has fallen
    // spoils the premise and hands the player a map of a world they have no
    // reason to care about.
    unlocked: (save) => save.statistics.clearedActs.length > 0,
    requirement: 'Clear an act first.',
  },
];

/** Play hub: Start Run (-> character select), Undersong Tide, Seeded Run (daily or typed), Boss Challenge (-> boss select), Card Practice (-> spell-card list), Leaderboard, and Reprise (the collective world-rebuild). */
export class PlayMenu {
  constructor(root, save, handlers) {
    const { onBack } = handlers;
    this.root = root;
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay play-menu-overlay';
    this.el.innerHTML = `
      <div class="menu-kicker">RUN OPERATIONS</div>
      <div class="menu-title">PLAY</div>
      <div class="menu-subtitle">Choose a route into the gauntlet.</div>
      <div class="play-menu-cards"></div>
    `;

    const cards = this.el.querySelector('.play-menu-cards');
    for (const mode of MODES) {
      const unlocked = !mode.unlocked || mode.unlocked(save);
      const card = document.createElement('button');
      card.type = 'button';
      card.dataset.action = mode.action;
      card.className = `menu-button play-mode-card${mode.primary ? ' play-mode-card-primary' : ''}${unlocked ? '' : ' play-mode-card-locked'}`;
      // Left navigable but inert rather than `disabled`: menuKeyNav skips
      // disabled buttons entirely, so a locked card would be invisible to a
      // keyboard player — including the requirement text, which is the only
      // reason to render it at all.
      card.setAttribute('aria-disabled', String(!unlocked));

      const icon = document.createElement('span');
      icon.className = 'play-mode-icon';
      icon.textContent = unlocked ? mode.icon : '✧';
      card.appendChild(icon);

      const body = document.createElement('span');
      const title = document.createElement('b');
      title.textContent = mode.title;
      body.appendChild(title);
      const blurb = document.createElement('small');
      blurb.textContent = mode.blurb;
      body.appendChild(blurb);
      if (!unlocked) {
        const req = document.createElement('small');
        req.className = 'play-mode-requirement';
        req.textContent = mode.requirement;
        body.appendChild(req);
      }
      card.appendChild(body);

      card.addEventListener('click', () => {
        if (!unlocked) {
          audio.playSfx('click');
          return;
        }
        audio.playSfx('click');
        handlers[handlerNameFor(mode.action)]();
      });
      cards.appendChild(card);
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); onBack(); });
    this.el.appendChild(back);

    this.root.appendChild(this.el);
  }

  destroy() {
    this.el.remove();
  }
}

/** `boss-challenge` -> `onBossChallenge`. */
function handlerNameFor(action) {
  return `on${action.split('-').map((part) => part[0].toUpperCase() + part.slice(1)).join('')}`;
}
